/**
 * Shared utility functions — consolidated from SPA code review (finding 2.1).
 */

/** Capitalize first letter of a string. */
export function cap(s: string | undefined | null): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Sanitize a string for safe HTML interpolation (finding 5.1). */
export function sanitize(s: string | undefined | null): string {
  if (!s) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Generate a UUID v4 using crypto.randomUUID with fallback (finding 5.5). */
export function generateGUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Debounce a function call. */
export function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

/** Environment label maps — consolidated from SPA (finding 2.1). */
export const ENV_LABELS = {
  audience: { internal: 'Internal', partners: 'Partners', public: 'Public' } as Record<string, string>,
  deployment: { cloud: 'Cloud Hosted', 'on-premise': 'On-Premise', 'edge-local': 'Edge / Local' } as Record<string, string>,
  context: { user: 'User', agent: 'Agent', global: 'Global' } as Record<string, string>,
  consent: { user: 'User', admin: 'Admin', none: 'None' } as Record<string, string>,
  interaction: { 'user-interactive': 'User Interactive', 'user-directed': 'User Directed', autonomous: 'Autonomous' } as Record<string, string>,
};

/** Worksheet status definitions — from UC_STATUSES. */
export const WORKSHEET_STATUSES = [
  { value: 'draft', label: 'Draft', color: '#64748b', bg: '#f1f5f9', darkBg: '#334155' },
  { value: 'in-progress', label: 'In Progress', color: '#3b82f6', bg: '#eff6ff', darkBg: '#1e3a5f' },
  { value: 'in-review', label: 'In Review', color: '#f59e0b', bg: '#fffbeb', darkBg: '#78350f' },
  { value: 'complete', label: 'Complete', color: '#10b981', bg: '#ecfdf5', darkBg: '#064e3b' },
  { value: 'archived', label: 'Archived', color: '#8b5cf6', bg: '#f5f3ff', darkBg: '#4c1d95' },
] as const;

/** Get status definition by value. */
export function getStatusDef(status: string) {
  return WORKSHEET_STATUSES.find((s) => s.value === status) || WORKSHEET_STATUSES[0];
}

/** Credential role definitions. */
export const CRED_ROLES: Record<string, string> = {
  origin: 'Origin',
  pass: 'Pass',
  exchange: 'Exchange',
  verify: 'Verify',
  terminate: 'End',
  'signal-in': 'Receive Signal',
  'signal-out': 'Send Signal',
  skip: 'Skip',
};

/** Control category labels and colors. */
export const CTRL_CATEGORIES: Record<string, { label: string; color: string }> = {
  authentication: { label: 'Authentication', color: '#3b82f6' },
  authorization: { label: 'Authorization', color: '#f59e0b' },
  'data-protection': { label: 'Data Protection', color: '#ef4444' },
  monitoring: { label: 'Monitoring', color: '#10b981' },
  governance: { label: 'Governance', color: '#8b5cf6' },
  policies: { label: 'Policies', color: '#ec4899' },
  credentials: { label: 'Credentials', color: '#64748b' },
};

/** Actor category labels. */
export const ACTOR_CATEGORIES: Record<string, string> = {
  'request-chain': 'Request Chain',
  'identity-access': 'Identity & Access',
  security: 'Security',
  governance: 'Governance',
  infrastructure: 'Infrastructure',
};

/** Wizard step names. */
export const STEPS = ['Describe', 'Environment', 'Actors', 'Controls & Actions'] as const;
